<script lang="ts">
  import { invalidateAll } from '$app/navigation';
  import { page } from '$app/stores';
  import type { SiteConfig } from '$types/races';
  import { updateSiteConfig } from '$utils/ui';
  import TablerPercentage from '~icons/tabler/percentage';

  $: ({ layoutConfigStore, user } = $page.data);

  let betfairReductionFactorInput: HTMLInputElement;

  type SiteConfigSubset = {
    [key in keyof SiteConfig]: SiteConfig[key];
  };

  async function handleSiteConfigChange(siteConfig: SiteConfigSubset) {
    if (!user) return;
    if (!user.data?.user) return;
    // any attribute not present in the new siteConfig will be set to its original value
    user.data.user.siteConfig = {
      ...user.data?.user.siteConfig,
      ...siteConfig
    };
    await updateSiteConfig();
    invalidateAll();
  }
</script>

<div class="flex flex-row items-center gap-x-2">
  <div class="flex-1">
    <h2 class="text-base font-semibold leading-7 text-gray-900">
      Site Configuration
    </h2>
    <p class="mt-1 max-w-2xl text-sm leading-6 text-gray-600">
      Configure the behaviour of the site. These settings will be the same no
      matter which layout you are using.
    </p>
  </div>
</div>
{#if user?.data?.user?.siteConfig}
  <div
    class="flex w-full flex-col space-y-8 border-b border-gray-900/10 pb-12 sm:space-y-0 sm:divide-y sm:divide-gray-900/10 sm:border-t sm:pb-0"
  >
    <button
      class="flex w-full cursor-pointer px-5 text-left hover:bg-gray-50 sm:gap-4 sm:py-6"
      on:click|preventDefault|capture|stopPropagation={() => {
        handleSiteConfigChange({
          hideAbandonedRaces: !user?.data?.user.siteConfig.hideAbandonedRaces
        });
      }}
    >
      <div class="min-w-0 flex-1 cursor-pointer text-sm">
        <label
          for="hideAbandonedRaces"
          class="cursor-pointer font-medium text-gray-700"
          >Hide Abandoned Races <p
            id="hideAbandonedRaces-description"
            class="cursor-pointer text-gray-500"
          >
            Hide abandoned races/meetings from the sidebar
          </p>
        </label>
      </div>
      <div class="ml-3 flex h-full cursor-pointer items-center">
        <input
          on:click|preventDefault|capture|stopPropagation
          bind:checked={user.data.user.siteConfig.hideAbandonedRaces}
          id="hideAbandonedRaces"
          aria-describedby="hideAbandonedRaces-description"
          name="hideAbandonedRaces"
          type="checkbox"
          class="h-5 w-5 cursor-pointer rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
        />
      </div>
    </button>
    <button
      class="flex w-full cursor-pointer px-5 text-left hover:bg-gray-50 sm:gap-4 sm:py-6"
      on:click|preventDefault={() => {
        handleSiteConfigChange({
          autoEventNav: !user?.data?.user.siteConfig.autoEventNav
        });
      }}
    >
      <div class="min-w-0 flex-1 cursor-pointer text-sm">
        <label
          for="autoEventNav"
          class="cursor-pointer font-medium text-gray-700"
          >Auto Event Navigation <p
            id="autoEventNav-description"
            class="cursor-pointer text-gray-500"
          >
            Automatically navigate to the next event when the current one
            finishes
          </p>
        </label>
      </div>
      <div class="ml-3 flex h-full cursor-pointer items-center">
        <input
          bind:checked={user.data.user.siteConfig.autoEventNav}
          id="autoEventNav"
          aria-describedby="autoEventNav-description"
          name="autoEventNav"
          type="checkbox"
          class="h-5 w-5 cursor-pointer rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
        />
      </div>
    </button>
    <button
      class="flex w-full cursor-pointer px-5 text-left hover:bg-gray-50 sm:gap-4 sm:py-6"
      on:click|preventDefault={() => {
        handleSiteConfigChange({
          relativeEventTime: !user?.data?.user.siteConfig.relativeEventTime
        });
      }}
    >
      <div class="min-w-0 flex-1 cursor-pointer text-sm">
        <label
          for="relativeEventTime"
          class="cursor-pointer font-medium text-gray-700"
          >Relative Event Time <p
            id="relativeEventTime-description"
            class="cursor-pointer text-gray-500"
          >
            Show the time until the event starts instead of the absolute time
          </p>
        </label>
      </div>
      <div class="ml-3 flex h-full cursor-pointer items-center">
        <input
          bind:checked={user.data.user.siteConfig.relativeEventTime}
          id="relativeEventTime"
          aria-describedby="relativeEventTime-description"
          name="relativeEventTime"
          type="checkbox"
          class="h-5 w-5 cursor-pointer rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
        />
      </div>
    </button>
    <button
      class="flex w-full cursor-pointer px-5 text-left hover:bg-gray-50 sm:gap-4 sm:py-6"
      on:click|preventDefault={() => {
        handleSiteConfigChange({
          alternateColourMode: !user?.data?.user.siteConfig.alternateColourMode
        });
      }}
    >
      <div class="min-w-0 flex-1 cursor-pointer text-sm">
        <label
          for="alternateColourMode"
          class="cursor-pointer font-medium text-gray-700"
          >Alternate Colour Mode <p
            id="alternateColourMode-description"
            class="cursor-pointer text-gray-500"
          >
            Replace red/green colours across the site
          </p>
        </label>
      </div>
      <div class="ml-3 flex h-full cursor-pointer items-center">
        <input
          bind:checked={user.data.user.siteConfig.alternateColourMode}
          id="alternateColourMode"
          aria-describedby="alternateColourMode-description"
          name="alternateColourMode"
          type="checkbox"
          class="h-5 w-5 cursor-pointer rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
        />
      </div>
    </button>
    <!-- betfairReductionFactor takes a numeric input -->
    <div
      class="flex w-full cursor-pointer px-5 hover:bg-gray-50 sm:gap-4 sm:py-6"
    >
      <div class="min-w-0 flex-1 cursor-pointer text-sm">
        <label
          for="betfairReductionFactor"
          class="cursor-pointer font-medium text-gray-700"
          >Betfair Reduction Factor <p
            id="betfairReductionFactor-description"
            class="cursor-pointer text-gray-500"
          >
            Reduce displayed Betfair odds by a certain % to account for
            commissions
          </p>
        </label>
      </div>
      <div
        role="textbox"
        tabindex="0"
        class="relative ml-3 flex h-full cursor-text items-center"
        on:click={() => {
          betfairReductionFactorInput.focus();
        }}
        on:keypress={() => {
          betfairReductionFactorInput.focus();
        }}
      >
        <input
          bind:this={betfairReductionFactorInput}
          bind:value={user.data.user.siteConfig.betfairReductionFactor}
          on:blur={() => {
            handleSiteConfigChange({
              betfairReductionFactor: Number(
                user?.data?.user.siteConfig.betfairReductionFactor
              )
            });
          }}
          id="betfairReductionFactor"
          aria-describedby="betfairReductionFactor-description"
          name="betfairReductionFactor"
          type="number"
          step="1"
          class="w-24 cursor-pointer rounded border-gray-300 focus:ring-indigo-500"
        />
        <div
          class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-gray-600 sm:text-sm"
        >
          <TablerPercentage />
        </div>
      </div>
    </div>
  </div>
{/if}
